/**
 * 글 본문 HTML에서 alt가 없거나 빈 <img>에 대체 텍스트를 채운다.
 *
 * 에디터로 올린 본문 이미지는 거의 alt가 비어 있어, 이미지 검색·접근성에서
 * 무엇을 담은 이미지인지 알 수 없다. DB는 건드리지 않고 렌더링 때만 보완한다.
 * - 값: `글 제목 - 바로 위 소제목(h1~h4)`. 소제목이 없으면 글 제목만 쓴다.
 * - 같은 소제목 아래 여러 장이면 두 번째부터 ` (2)`, ` (3)`을 붙여 겹치지 않게 한다.
 * - 편집자가 직접 넣은 alt는 그대로 둔다.
 */
/** alt에 넣는 소제목 최대 글자 수 */
const HEADING_MAX_LENGTH = 50;

export function fillMissingImageAlt(html: string, title: string): string {
  const baseTitle = title.replace(/\s+/g, ' ').trim();
  if (!baseTitle) return html;

  let heading = '';
  let countUnderHeading = 0;

  const fillImage = (tag: string) => {
    const altMatch = tag.match(/\salt\s*=\s*(["'])([\s\S]*?)\1/i);
    if (altMatch && altMatch[2].trim()) return tag;

    countUnderHeading += 1;
    const text =
      (heading ? `${baseTitle} - ${heading}` : baseTitle) +
      (countUnderHeading > 1 ? ` (${countUnderHeading})` : '');
    const altAttribute = ` alt="${escapeAttribute(text)}"`;

    return altMatch
      ? tag.replace(altMatch[0], altAttribute)
      : tag.replace(/^<img\b/i, `<img${altAttribute}`);
  };

  return html.replace(
    /<(h[1-4])\b[^>]*>([\s\S]*?)<\/\1>|<img\b[^>]*>/gi,
    (match, headingTag: string | undefined, headingHtml: string) => {
      if (!headingTag) return fillImage(match);

      heading = toPlainText(headingHtml);
      countUnderHeading = 0;
      // 에디터가 이미지를 소제목 태그 안에 넣은 경우(<h3>코스 약도<img></h3>)도 채운다.
      return match.replace(/<img\b[^>]*>/gi, fillImage);
    },
  );
}

/** 소제목 HTML을 한 줄 텍스트로. 엔티티는 풀어 두고 속성에 넣을 때 다시 이스케이프한다. */
function toPlainText(html: string): string {
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
  // 에디터가 문단째 소제목에 넣은 글도 있어, 너무 길면 단어 경계에서 자른다.
  if (text.length <= HEADING_MAX_LENGTH) return text;
  const cut = text.slice(0, HEADING_MAX_LENGTH);
  const lastSpace = cut.lastIndexOf(' ');
  return (
    lastSpace > HEADING_MAX_LENGTH * 0.6 ? cut.slice(0, lastSpace) : cut
  ).trim();
}

function escapeAttribute(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
